// PRD-RTE-TIPTAP-document-model §TR3
// Migration shim from legacy `TextBox.text` (HTML string) to TipTap JSON.

import { generateJSON } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';

import { DeferredFormatsExt } from './tiptap-extensions.js';
import type {
  TextDocument,
  TextDocumentNode,
  TextDocumentMark,
} from './text-document.type.js';
import type { ListType } from './textbox.interface.js';

// Legacy container-level format properties carried on a pre-TipTap `TextBox`.
// Passed to `textToDocument` so non-default values can be pushed down onto the
// produced text runs as per-run textStyle marks (PRD-RTE-TIPTAP-MARKS §TR4).
export interface LegacyFormatDefaults {
  readonly fontWeight?: number | string;
  readonly textTransform?: string;
  readonly verticalScript?: string;
}

// Minimal extension set sufficient for parsing legacy textbox HTML.
// Full TEXTBOX_EXTENSIONS registry comes in T1.5; T1.3 wires the migration
// shim with the subset needed for current RED tests (bold, plain runs).
// StarterKit (v3) ships Underline + UndoRedo by default; we disable UndoRedo
// because @o2d/history manages undo at board level.
const MIGRATION_EXTENSIONS = [
  // `trailingNode: false` — same fix as `TEXTBOX_EXTENSIONS` (tiptap-extensions.ts):
  // legacy HTML that happens to end on a list must not gain a phantom empty
  // paragraph baked permanently into the migrated document.
  StarterKit.configure({ undoRedo: false, trailingNode: false }),
  TextStyle,
  Color,
  DeferredFormatsExt,
];
// This list is hand-maintained and independent of `createRichTextExtensions()`,
// so nothing keeps it in step automatically. `FontFamily` was dropped here with
// @o2d/rich-text-editor 0.3.0: a font family captured from legacy HTML would be
// discarded silently by the schema on the next save. What holds this is
// `tests/unit/features/textbox/legacy-font-family-dropped.test.ts`.

const EMPTY_PARAGRAPH_DOC: TextDocument = Object.freeze({
  type: 'doc',
  content: [{ type: 'paragraph' }],
}) as TextDocument;

function emptyDoc(): TextDocument {
  return { type: 'doc', content: [{ type: 'paragraph' }] };
}

function plainTextDoc(text: string): TextDocument {
  return {
    type: 'doc',
    content: [{ type: 'paragraph', content: [{ type: 'text', text }] }],
  };
}

// Split legacy list text on newlines and drop empty trailing entries.
function splitLines(text: string): string[] {
  const lines = text.split(/\r?\n/);
  while (lines.length > 1 && lines[lines.length - 1]?.trim() === '') {
    lines.pop();
  }
  return lines;
}

// Build a single listItem node wrapping a paragraph with the given text run.
function listItemNode(text: string): TextDocumentNode {
  const paragraph =
    text.length === 0
      ? { type: 'paragraph' }
      : { type: 'paragraph', content: [{ type: 'text', text }] };
  return { type: 'listItem', content: [paragraph] };
}

// Build a taskItem with explicit `checked` attr. Strips the "[ ] " / "[x] " prefix.
const CHECKLIST_PREFIX = /^\s*\[( |x|X)\]\s?/;
function taskItemNode(rawLine: string): TextDocumentNode {
  const match = rawLine.match(CHECKLIST_PREFIX);
  const checked = match ? match[1]?.toLowerCase() === 'x' : false;
  const text = match ? rawLine.slice(match[0].length) : rawLine;
  const paragraph =
    text.length === 0
      ? { type: 'paragraph' }
      : { type: 'paragraph', content: [{ type: 'text', text }] };
  return { type: 'taskItem', attrs: { checked }, content: [paragraph] };
}

// Build the textStyle mark attrs for non-default legacy format values.
// Returns null when every value is at its container default (no mark needed).
function legacyMarkAttrs(
  legacyDefaults: LegacyFormatDefaults,
): Record<string, string> | null {
  const attrs: Record<string, string> = {};
  const { fontWeight, textTransform, verticalScript } = legacyDefaults;
  if (fontWeight !== undefined && String(fontWeight) !== '500') {
    attrs.fontWeight = String(fontWeight);
  }
  if (textTransform !== undefined && textTransform !== 'none') {
    attrs.textTransform = textTransform;
  }
  if (verticalScript !== undefined && verticalScript !== 'none') {
    attrs.verticalScript = verticalScript;
  }
  return Object.keys(attrs).length > 0 ? attrs : null;
}

// Recursively clone a node, merging the given textStyle attrs into every
// text run's mark set. An existing textStyle mark is extended; otherwise a
// new textStyle mark is appended.
function injectTextStyleMark(
  node: TextDocumentNode,
  attrs: Record<string, string>,
): TextDocumentNode {
  if (node.type === 'text') {
    const marks: TextDocumentMark[] = [...(node.marks ?? [])];
    const idx = marks.findIndex((m) => m.type === 'textStyle');
    if (idx >= 0) {
      marks[idx] = {
        type: 'textStyle',
        attrs: { ...(marks[idx]?.attrs ?? {}), ...attrs },
      };
    } else {
      marks.push({ type: 'textStyle', attrs: { ...attrs } });
    }
    return { ...node, marks };
  }
  if (node.content) {
    return { ...node, content: node.content.map((c) => injectTextStyleMark(c, attrs)) };
  }
  return node;
}

/**
 * Convert a legacy textbox HTML/text string into a TipTap JSON document.
 *
 * @param html  Legacy `TextBox.text` value (may be HTML, plain text, or empty).
 * @param listType  Optional legacy `TextBox.listType` for list migration.
 * @param legacyDefaults  Optional container-level format properties; non-default
 *   `fontWeight` / `textTransform` / `verticalScript` values are pushed down onto
 *   the produced text runs as per-run textStyle marks. Ignored for list branches.
 */
export function textToDocument(
  html: string,
  listType?: ListType,
  legacyDefaults?: LegacyFormatDefaults,
): TextDocument {
  if (!html || html.trim().length === 0) {
    return emptyDoc();
  }

  // List branch: convert newline-separated items into list nodes (T6.2).
  if (listType === 'bullet' || listType === 'ordered') {
    const wrapperType = listType === 'bullet' ? 'bulletList' : 'orderedList';
    return {
      type: 'doc',
      content: [{ type: wrapperType, content: splitLines(html).map(listItemNode) }],
    };
  }
  if (listType === 'checklist') {
    return {
      type: 'doc',
      content: [{ type: 'taskList', content: splitLines(html).map(taskItemNode) }],
    };
  }

  const doc =
    typeof DOMParser === 'undefined'
      ? plainTextDoc(html)
      : (generateJSON(html, MIGRATION_EXTENSIONS) as TextDocument);

  const markAttrs = legacyDefaults ? legacyMarkAttrs(legacyDefaults) : null;
  if (!markAttrs) {
    return doc;
  }
  return {
    type: 'doc',
    content: (doc.content ?? []).map((n) => injectTextStyleMark(n, markAttrs)),
  };
}

// Re-export the frozen empty doc for callers that need a shared sentinel.
export { EMPTY_PARAGRAPH_DOC };

// Map a top-level node type to its advisory list kind, or null for non-lists.
function listKindOf(type: string): ListType | null {
  if (type === 'bulletList') return 'bullet';
  if (type === 'orderedList') return 'ordered';
  if (type === 'taskList') return 'checklist';
  return null;
}

/**
 * O2D.W-PRD-035 §TR3 — derive the ADVISORY, non-load-bearing `obj.listType`
 * scalar from a committed TipTap document. The document is authoritative for
 * list structure; this scalar is only a hint:
 *  - a UNIFORM document (every top-level block the same single list kind) →
 *    that kind;
 *  - a MIXED document (differing kinds, or any plain paragraph mixed with list
 *    nodes) → `'none'` (a single scalar cannot represent a mixed doc);
 *  - empty / plain → `'none'`.
 *
 * Pure over a `TextDocument` — never mutates it, so it is independently
 * unit-testable over any document/JSON.
 */
export function deriveAdvisoryListType(doc: TextDocument): ListType {
  const content = doc.content ?? [];
  // Ignore trailing EMPTY paragraph(s): StarterKit's `trailingNode` extension
  // auto-appends an empty paragraph after a list node (a document cannot end on
  // a list), which would otherwise make a uniform list look MIXED and derive
  // `'none'` — breaking the checklist hit-test's `listType === 'checklist'`
  // gate (TR6). A trailing empty paragraph is an editor artifact, not list
  // structure; a paragraph carrying TEXT still counts as genuine mixing.
  let end = content.length;
  while (end > 0) {
    const last = content[end - 1]!;
    if (last.type === 'paragraph' && itemPlainText(last).trim() === '') {
      end -= 1;
      continue;
    }
    break;
  }
  const blocks = content.slice(0, end);
  if (blocks.length === 0) return 'none';
  const first = listKindOf(blocks[0]!.type);
  if (first === null) return 'none';
  for (const node of blocks) {
    if (listKindOf(node.type) !== first) return 'none';
  }
  return first;
}

// Concatenate the plain-text content of a single list-item-like node.
function itemPlainText(item: TextDocumentNode): string {
  let buf = '';
  const walk = (n: TextDocumentNode): void => {
    if (typeof n.text === 'string') buf += n.text;
    if (n.content) for (const c of n.content) walk(c);
  };
  walk(item);
  return buf;
}

// Flatten a top-level block into its child content blocks: a list node yields
// each of its items' child blocks (verbatim); a loose block yields itself.
function toChildBlocks(block: TextDocumentNode): TextDocumentNode[] {
  if (listKindOf(block.type) === null) return [block];
  const children: TextDocumentNode[] = [];
  for (const item of block.content ?? []) {
    for (const child of item.content ?? []) children.push(child);
  }
  return children;
}

/**
 * O2D.W-PRD-037 §TR1 — reshape a committed TipTap document's top-level list
 * structure to `targetType`, WITHOUT ever reading the legacy `TextBox.text`.
 *
 * This is the content-loss hotfix: the old `setListType` reshaped the stale
 * `obj.text` (`''` post-O2D.W-PRD-035) through `textToDocument`, whose empty-guard
 * fired → `emptyDoc()` → content loss. This helper reshapes the AUTHORITATIVE
 * `obj.document` instead.
 *
 * Algorithm:
 *  1. nullish `doc` → `emptyDoc()`;
 *  2. drop empty/artifact top-level blocks FIRST (`itemPlainText(b).trim() === ''`),
 *     covering the `trailingNode` empty paragraph an editor auto-appends;
 *  3. no blocks remain → `emptyDoc()` (never a spurious one-item list);
 *  4. `targetType === 'none'` → unwrap: list nodes contribute their items' child
 *     blocks verbatim, loose blocks stay as-is;
 *  5. otherwise FLATTEN to child blocks and wrap EACH verbatim into ONE list of the
 *     target kind — `bullet`/`ordered` → `listItem`, `checklist` → `taskItem`
 *     (`checked:false`). Inline content (text + marks) is preserved verbatim: NEW
 *     nodes are built (types are `readonly`), never mutated, and the mark-dropping
 *     `listItemNode`/`taskItemNode` string builders are NOT used.
 *
 * Pure over a `TextDocument` — never mutates its input.
 */
export function reshapeDocumentListType(
  doc: TextDocument,
  targetType: ListType,
): TextDocument {
  if (!doc || !doc.content) return emptyDoc();

  // Drop empty/artifact top-level blocks FIRST (before any classify/wrap).
  const blocks = doc.content.filter((b) => itemPlainText(b).trim() !== '');
  if (blocks.length === 0) return emptyDoc();

  // Toggle-off: unwrap list nodes to their items' child blocks, keep loose blocks.
  if (targetType === 'none') {
    const unwrapped = blocks.flatMap(toChildBlocks);
    return { type: 'doc', content: unwrapped };
  }

  // Wrap: flatten to child blocks, then wrap each into ONE list of the target kind.
  const childBlocks = blocks.flatMap(toChildBlocks);
  const wrapperType =
    targetType === 'bullet'
      ? 'bulletList'
      : targetType === 'ordered'
        ? 'orderedList'
        : 'taskList';
  const items: TextDocumentNode[] = childBlocks.map((child) =>
    targetType === 'checklist'
      ? { type: 'taskItem', attrs: { checked: false }, content: [child] }
      : { type: 'listItem', content: [child] },
  );
  return { type: 'doc', content: [{ type: wrapperType, content: items }] };
}

/**
 * O2D.W-PRD-035 §TR6 — count the VISIBLE (non-blank) `taskItem` nodes in a
 * document. Reuses the doc-walk STRUCTURE of {@link documentToListText} (iterate
 * `doc.content`, match the `taskList` wrapper, iterate its `taskItem` children)
 * but adds a non-blank filter via `itemPlainText(item).trim() !== ''` — the SAME
 * predicate the toggle consumer (`taskItemHasVisibleContent`) uses, so the
 * hit-test producer's line count matches the consumer's `visibleIndex`. The
 * legacy `.replace(/<[^>]+>/g, '')` HTML strip is NOT needed here — doc-model
 * `taskItem` text carries no HTML tags. Pure over the document.
 */
export function countVisibleTaskItems(doc: TextDocument): number {
  let count = 0;
  for (const node of doc.content ?? []) {
    if (node.type !== 'taskList') continue;
    for (const item of node.content ?? []) {
      if (itemPlainText(item).trim() !== '') count += 1;
    }
  }
  return count;
}

/**
 * Inverse of `textToDocument`'s list branch: derive the legacy
 * newline-separated `TextBox.text` representation from a TipTap document.
 * Used by the editor commit path while the deprecated `text` field is still
 * read by sticky-bridge and legacy tests (Phase 9 will retire this).
 */
export function documentToListText(doc: TextDocument, listType: ListType): string {
  const wrapperTypes =
    listType === 'checklist'
      ? ['taskList']
      : listType === 'ordered'
        ? ['orderedList']
        : ['bulletList'];
  const items: string[] = [];
  for (const node of doc.content ?? []) {
    if (!wrapperTypes.includes(node.type)) continue;
    for (const item of node.content ?? []) {
      const text = itemPlainText(item);
      if (listType === 'checklist') {
        const checked = (item.attrs as { checked?: unknown } | undefined)?.checked === true;
        items.push(`${checked ? '[x]' : '[ ]'} ${text}`);
      } else {
        items.push(text);
      }
    }
  }
  return items.join('\n');
}
